// src/LoginPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import LandingLayout from "./landing/LandingLayout";

const API_BASE = process.env.REACT_APP_API_BASE || "https://zeitgeist.api";
const isAbsoluteUrl = (v: string) => /^https?:\/\//i.test(v);

export default function LoginPage() {
  const { loginWithRedirect, isAuthenticated, user, getAccessTokenSilently } = useAuth0();

  const [booting, setBooting] = useState(false);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  // Parse query params (Auth0 redirects may include state/code)
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);

  // Show verify banner if redirected back with verify error.
  // Persist a flag because Auth0 SDK/router can rewrite the URL quickly.
  const verifyEmailError = useMemo(() => {
    const err = urlParams.get("error") || "";
    const errDesc = (urlParams.get("error_description") || "").toLowerCase();

    const hit =
      err === "verify_email" ||
      err === "verify-your-email" ||
      err === "unverified_email" ||
      (err === "access_denied" && errDesc.includes("verify")) ||
      errDesc.includes("verify") ||
      errDesc.includes("verification");

    if (hit) {
      try {
        sessionStorage.setItem("zg_verify_email", "1");
        const rt = urlParams.get("returnTo");
        if (rt) sessionStorage.setItem("zg_return_to", rt);
      } catch {}
    }

    try {
      return hit || sessionStorage.getItem("zg_verify_email") === "1";
    } catch {
      return hit;
    }
  }, [urlParams]);

  // If we are showing the verify banner, clean the URL from Auth0 transaction params.
  // This avoids "stuck" callback/state issues when the user clicks "weiter".
  useEffect(() => {
    if (!verifyEmailError) return;

    const url = new URL(window.location.href);

    const rt = url.searchParams.get("returnTo");
    if (rt) {
      try {
        sessionStorage.setItem("zg_return_to", rt);
      } catch {}
    }

    url.searchParams.delete("error");
    url.searchParams.delete("error_description");
    url.searchParams.delete("state");
    url.searchParams.delete("code");

    window.history.replaceState({}, "", url.toString());
  }, [verifyEmailError]);

  const dismissVerifyEmailBanner = useCallback(() => {
    try {
      sessionStorage.removeItem("zg_verify_email");
      sessionStorage.removeItem("zg_return_to");
    } catch {}

    const url = new URL(window.location.href);
    url.searchParams.delete("error");
    url.searchParams.delete("error_description");
    url.searchParams.delete("state");
    url.searchParams.delete("code");
    window.history.replaceState({}, "", url.toString());

    
    // Force UI to fully reset (and re-read URL/session flags).
    window.location.reload();
    
  }, []);

  // After the user verifies in email and clicks "weiter": start a fresh Auth0 login.
  // Always return into the app first; if forum returnTo was provided, we redirect after login.
  const continueAfterVerify = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        ui_locales: "de",
        scope: "openid profile email read:project write:rules",
        redirect_uri: window.location.origin,
      },
      appState: { returnTo: "/" },
    });
  }, [loginWithRedirect]);

  // Normal login (no forced prompt)
  const handleLogin = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        ui_locales: "de",
        scope: "openid profile email read:project write:rules",
        redirect_uri: window.location.origin,
      },
      appState: { returnTo: "/" },
    });
  }, [loginWithRedirect]);

  // True signup (forces Sign-Up tab)
  const handleSignup = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: "signup",
        prompt: "login",
        max_age: 0,
        ui_locales: "de",
        redirect_uri: window.location.origin,
        scope: "openid profile email offline_access read:project write:rules",
      },
    });
  }, [loginWithRedirect]);

  // If user came from forum flow and we stored an absolute returnTo, redirect after auth.
  useEffect(() => {
    if (!isAuthenticated) return;

    let rt = "";
    try {
      rt = sessionStorage.getItem("zg_return_to") || "";
    } catch {}

    if (rt && isAbsoluteUrl(rt)) {
      try {
        sessionStorage.removeItem("zg_return_to");
        sessionStorage.removeItem("zg_verify_email");
      } catch {}
      window.location.assign(rt);
    }
  }, [isAuthenticated]);

  // After Auth0 login, fetch an access token & call backend /login
  useEffect(() => {
    const bootstrap = async () => {
      if (!isAuthenticated || projectId) return;
      try {
        setBooting(true);
        setBootError(null);

        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: "https://zeitgeist.api",
            scope: "read:project write:rules",
          },
        });

        const res = await fetch(`${API_BASE}/login`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`${res.status} ${txt}`);
        }

        const boot = await res.json();
        setProjectId(boot.project_id);

        localStorage.setItem("zg_project_id", String(boot.project_id));
        localStorage.setItem("zg_rules_xml", boot.xml);
        localStorage.setItem("zg_solution_csv", boot.csv);
        localStorage.setItem("zg_q_json", JSON.stringify(boot.q));

        // If returnTo is a *relative* path (in-app), you can navigate there via your router.
        // For now, the appState returnTo already lands on /plan.
      } catch (e: any) {
        setBootError(e?.message || "Bootstrap failed");
        console.error("Bootstrap error", e);
      } finally {
        setBooting(false);
      }
    };
    bootstrap();
  }, [isAuthenticated, projectId, getAccessTokenSilently]);

  return (
    <LandingLayout
      isAuthenticated={isAuthenticated}
      userEmail={user?.email || ""}
      booting={booting}
      projectId={projectId}
      bootError={bootError}
      verifyEmailError={!!verifyEmailError}
      onSignup={handleSignup}
      onLogin={handleLogin}
      onContinueAfterVerify={continueAfterVerify}
      onDismissVerifyEmailBanner={dismissVerifyEmailBanner}
    />
  );
}